/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useState, useEffect, ReactNode } from 'react'
import { routes } from 'src/components/DashboardMenu/Routes'
import {
  ILocalRoutes,
  IPermissions,
} from 'src/components/Sidebar/Sidebar.Interface'
import useIsMounted from 'src/hooks/useIsMounted'
import { getUserPermissions } from 'src/services/IAM/AdminPermissions/AdminPermissions.service'
import { useAlert } from 'src/hooks/useAlert'
import { profile } from 'src/services/User/user.service'
import { User } from 'src/services/User/user.interface'
import useLoading from 'src/hooks/useLoading'

/**
 * Tipo de datos para el contexto de autenticación.
 * 
 * @interface
 * @property {boolean} isAuthenticated - Indica si el usuario está autenticado.
 * @property {User | null} user - Información del usuario autenticado.
 * @property {ILocalRoutes[]} userRoutes - Rutas autorizadas para el usuario.
 * @property {function} setUserRoutes - Establece las rutas del usuario.
 * @property {function} setIsAuthenticated - Establece el estado de autenticación.
 * @property {function} setUser - Establece la información del usuario.
 */
export interface AuthContextType {
  isAuthenticated: boolean
  user: null | User
  userRoutes: ILocalRoutes[]
  setUserRoutes: (routes: ILocalRoutes[]) => void
  setIsAuthenticated: (authenticated: boolean) => void
  setUser: (user: AuthContextType['user']) => void
}

/**
 * Contexto de autenticación para gestionar la sesión del usuario.
 * 
 * @type {React.Context<AuthContextType>}
 */
export const AuthContext = createContext<AuthContextType>({} as AuthContextType)

/**
 * Proveedor de autenticación que gestiona el estado de autenticación y las rutas del usuario.
 * 
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {ReactNode} props.children - Elementos hijos que serán envueltos por el proveedor de autenticación.
 * @returns {JSX.Element} Componente proveedor de autenticación.
 * 
 * @example
 * ```tsx
 * import { AuthProvider } from './AuthContext';
 * 
 * const App = () => (
 *   <AuthProvider>
 *     <YourComponent />
 *   </AuthProvider>
 * );
 * ```
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [user, setUser] = useState<User | null>(null)
  const [userRoutes, setUserRoutes] = useState<ILocalRoutes[]>([])
  const { showAlert } = useAlert()
  const isMounted = useIsMounted()
  const { startLoading, stopLoading } = useLoading()

  /**
   * Combina las rutas locales con los permisos del usuario.
   * 
   * @function
   * @param {ILocalRoutes[]} localRoutes - Rutas locales de la aplicación.
   * @param {IPermissions[]} permissions - Permisos del usuario.
   * @returns {ILocalRoutes[]} Rutas combinadas con los permisos.
   */
  function mergeModules(
    localRoutes: ILocalRoutes[],
    permissions: IPermissions[],
  ): ILocalRoutes[] {
    if (process.env.REACT_APP_ENV === 'dev') {
      return localRoutes
    } else {
      const mergedArray: ILocalRoutes[] = []
      if (permissions.length) {
        permissions.forEach((mod1) => {
          const mod2 = localRoutes.find((m) => m.keyName === mod1.keyName)

          if (mod2) {
            const newModule: ILocalRoutes = {
              keyName: mod1.keyName,
              moduleId: mod1.moduleId,
              subModules: [],
              icon: mod2.icon,
              title: mod2.title,
              route: mod2.route,
            }

            mod1?.subModules?.forEach((subMod1) => {
              const subMod2 = mod2?.subModules?.find(
                (sm) => sm.keyName === subMod1.keyName,
              )

              if (subMod2) {
                const newSubModule: ILocalRoutes = {
                  keyName: subMod1.keyName,
                  moduleId: subMod1.moduleId,
                  permission: subMod1.permission,
                  title: subMod2.title,
                  icon: subMod2.icon,
                  description: subMod2.description,
                  route: subMod2.route,
                  disabled: subMod2.disabled,
                }

                newModule?.subModules?.push(newSubModule)
              }
            })

            if (newModule) {
              mergedArray.push(newModule)
            }
          }
        })
      }
      return mergedArray
    }
  }

  /**
   * Efecto que se ejecuta al montar el componente para verificar la autenticación y cargar permisos.
   */
  useEffect(() => {
    if (!isMounted) return
    if (isAuthenticated) {
      startLoading()
      const fetchPermissions = async () => {
        try {
          const permissions = await getUserPermissions()
          if (permissions) {
            setUserRoutes(mergeModules(routes, permissions))
            showAlert('success', 'Rutas cargadas correctamente')
          }
          const user = await profile()
          setUser(user)
        } catch (error) {
          showAlert('error', 'Error al obtener los permisos')
        } finally {
          stopLoading()
        }
      }

      fetchPermissions()
    }
  }, [isAuthenticated, isMounted])

  /**
   * Efecto para verificar el token de acceso en el almacenamiento local y establecer la autenticación.
   */
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken')
    if (accessToken && accessToken !== 'null') {
      setIsAuthenticated(true)
      setUser(JSON.parse(localStorage.getItem('user') || '{}'))
    } else {
      setIsAuthenticated(false)
      setUser(null)
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        userRoutes,
        setUserRoutes,
        setIsAuthenticated,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
